import { ChevronLeft, ChevronRight, LogOut } from "lucide-react";

import type { SidebarProps } from "../types/sidebar.types";
import { useSidebarHook } from "../hooks/useSidebarHook";

export default function Sidebar({
  activeMenuItem,
  setActiveMenuItem,
  onItemClick,
}: SidebarProps) {
  const {
    mounted,
    collapsed,
    hoveredItem,
    loggingOut,
    visibleItems,
    initial,
    user,
    setHoveredItem,
    handleMenuItemClick,
    handleLogout,
    toggleCollapsed,
  } = useSidebarHook({
    activeMenuItem,
    setActiveMenuItem,
    onItemClick,
  });

  if (!mounted) {
    return (
      <div className="h-full w-[150px] rounded-[6px] bg-white shadow-sm sm:w-[165px] md:w-[180px]" />
    );
  }

  const sidebarWidth = collapsed
    ? "w-[64px] sm:w-[72px] md:w-[72px]"
    : "w-[180px] sm:w-[190px] md:w-[200px]";

  return (
    <div
      className={`${sidebarWidth} flex h-full flex-col overflow-hidden rounded-[6px] bg-white shadow-sm transition-[width] duration-200 md:rounded-[8px]`}
    >
      <div
        className={`flex items-center gap-2 px-5 pb-3 pt-5 sm:px-6 sm:pb-4 sm:pt-6 ${
          collapsed ? "justify-center" : "justify-between"
        }`}
      >
        <div className="flex items-center gap-3">
          {!collapsed && (
            <>
              <div className="flex h-7 w-7 items-center justify-center rounded-[6px] bg-[linear-gradient(135deg,#e80029_0%,#d1001f_100%)] sm:h-[30px] sm:w-[30px] md:rounded-[8px]">
                <span className="text-[0.8rem] font-bold text-white sm:text-[0.9rem]">
                  {initial}
                </span>
              </div>

              <h2
                className="max-w-[120px] truncate font-semibold text-slate-800"
                title={user?.firstName ?? "User"}
              >
                {user?.firstName ?? "User"}
              </h2>
            </>
          )}
        </div>

        <button
          type="button"
          onClick={toggleCollapsed}
          title={collapsed ? "Expand" : "Collapse"}
          className="rounded-md border border-slate-200 p-1 transition hover:bg-slate-50"
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-3 pb-3 pt-1 sm:px-4">
        <div className="space-y-2">
          {visibleItems.map((item, index) =>
            item.kind === "label" ? (
              <div
                key={`label-${index}`}
                className={`${collapsed ? "hidden" : "block"} mb-3 mt-5`}
              >
                <div className="border-t border-slate-200" />
                <p className="mt-4 px-2 text-[11px] font-bold tracking-[0.6px] text-slate-400">
                  {item.label}
                </p>
              </div>
            ) : (
              <button
                key={item.name}
                type="button"
                title={collapsed ? item.name : ""}
                onClick={() => handleMenuItemClick(item)}
                onMouseEnter={() => setHoveredItem(item.name)}
                onMouseLeave={() => setHoveredItem(null)}
                className={`mb-3 flex w-full items-center rounded-[6px] px-3 py-[10px] text-left transition-all duration-200 md:rounded-[8px] ${
                  collapsed ? "justify-center" : "justify-start"
                } ${
                  activeMenuItem === item.name
                    ? "bg-[linear-gradient(135deg,#e80029_0%,#d1001f_100%)] text-white"
                    : hoveredItem === item.name
                      ? "bg-[rgba(232,0,41,0.08)] text-[#e80029]"
                      : "text-slate-500"
                }`}
              >
                <span
                  className={`flex items-center justify-center ${
                    collapsed ? "" : "mr-4 min-w-[20px] sm:min-w-[24px]"
                  }`}
                >
                  <item.icon size={20} />
                </span>

                {!collapsed && (
                  <span
                    className={`truncate text-[0.8rem] sm:text-[0.86rem] ${
                      activeMenuItem === item.name
                        ? "font-semibold"
                        : "font-normal"
                    }`}
                  >
                    {item.name}
                  </span>
                )}
              </button>
            ),
          )}
        </div>
      </div>

      <div className="px-3 pb-3 sm:px-4">
        <button
          type="button"
          onClick={handleLogout}
          disabled={loggingOut}
          title={collapsed ? (loggingOut ? "Logging out..." : "Log out") : ""}
          className={`flex w-full items-center rounded-[6px] border border-slate-200 px-3 py-[10px] transition md:rounded-[8px] ${
            collapsed ? "justify-center" : "justify-start"
          } ${
            loggingOut
              ? "cursor-not-allowed opacity-70"
              : "text-slate-500 hover:border-red-500 hover:bg-red-50 hover:text-red-500"
          }`}
        >
          <span
            className={`flex items-center justify-center ${
              collapsed ? "" : "mr-4 min-w-[20px] sm:min-w-[24px]"
            }`}
          >
            <LogOut size={20} />
          </span>

          {!collapsed && (
            <span className="text-[0.8rem] font-medium sm:text-[0.86rem]">
              {loggingOut ? "Logging out..." : "Log out"}
            </span>
          )}
        </button>
      </div>
    </div>
  );
}
